<template>
  <div v-if="modelNotEmpty">
    <div class="subtitle1 my-2">{{ $t("general.filters") }}</div>
    <cui-filter-wrapper :render-clear-button="false" class="mt-2">
      <div v-if="exportType === ExportCategory.ACTIVITY_LOGS" class="d-flex flex-column">
        <activity-log-types-filter v-model="model.activityLogs.types" class="filter mb-2" />
      </div>
      <div v-else-if="exportType === ExportCategory.USERS" class="d-flex flex-column">
        <v-select
          v-model="model.users.cloudServices"
          :items="usersFilterItems"
          class="filter-menu cloud-services-filter mr-2"
          :class="{ 'filter-active': model.users.cloudServices?.length > 0 }"
          :placeholder="$t('general.cloudApps')"
          density="compact"
          variant="outlined"
          multiple
          rounded
          hide-details
          background-color="white"
          return-object
        >
          <template #selection="{ index }">
            <span v-if="index === 0" class="body2">{{ $t("general.cloudApps") }}</span>
          </template>
          <template #label>
            <span v-if="model.users.cloudServices?.length" class="filter-label">
              {{ model.users.cloudServices?.length }}
            </span>
          </template>
          <template #item="{ item, props }">
            <v-list-item v-bind="props" :title="$t(`services.${item.raw}`)">
              <template v-slot:prepend="{ isActive }">
                <v-list-item-action start>
                  <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
                </v-list-item-action>
              </template>
            </v-list-item>
          </template>
        </v-select>
      </div>
      <div v-else-if="exportType === ExportCategory.TICKETS" class="d-flex flex-column">
        <v-select
          v-model="model.tickets.widget"
          :items="widgets"
          :menu-props="{ maxHeight: '300' }"
          class="filter-menu widget-filter mt-2 fit"
          :class="{ 'filter-active': model.tickets.widget }"
          :placeholder="$t('tickets.filters.widgets.placeholder')"
          density="compact"
          item-value="name"
          item-title="name"
          rounded
          hide-details
          background-color="white"
        >
          <template #selection="{ item }">
            <span class="body2">
              {{ $t(`tickets.filters.widgets.${item.title}`) }}
            </span>
          </template>
          <template #item="{ item, props }">
            <v-list-item v-bind="props" :title="$t(`tickets.filters.widgets.${item.title}`)">
            </v-list-item>
          </template>
        </v-select>
        <ticket-type-filter
          v-model="model.tickets.eventTriggers"
          :triggers="triggers"
          class="triggers-filter mt-2"
        />
      </div>
      <export-devices-filters
        v-else-if="exportType === ExportCategory.DEVICES"
        v-model="model.devices"
        :labels="devicesSettingsStore.labels"
        :statuses="devicesStore.statuses"
        :raw-os-versions="osVersionsFromStore"
        :raw-client-versions="clientVersionsFromStore"
        hide-agent-health
      />
    </cui-filter-wrapper>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  type PropType,
  type Ref,
  ref,
  toRefs,
  watch,
} from "vue";
import { defaultFiltersState } from "@/_store/filters.module";
import { CuiFilterWrapper } from "@coronet/cui";
import { TicketTrigger, eventTriggerFilterMap, TicketsModuleFilter } from "@/constants/tickets";
import { useDevicesStore } from "@/_store/devices.module";
import { useDevicesSettingsStore } from "@/_store/devices-settings.module";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import type { ScheduledExportItemFilters } from "@/_store/scheduled-reports/scheduled-reports.module.ts";
import { defaultUsersExceptionsTabFilters } from "@/_store/users-settings/user-exceptions-tab.module";
import { Service } from "@/constants/cloud-apps.ts";
import { ExportCategory } from "@/constants/exports.ts";
import ExportDevicesFilters from "@/components/devices/ExportDevicesFilters.vue";
import TicketTypeFilter from "@/components/filters/TicketTypeFilter.vue";
import ActivityLogTypesFilter from "@/components/filters/ActivityLogTypesFilter.vue";

export default defineComponent({
  components: { CuiFilterWrapper, ExportDevicesFilters, TicketTypeFilter, ActivityLogTypesFilter },
  props: {
    exportType: {
      type: String as PropType<ExportCategory>,
      required: true,
    },
    filters: {
      type: Object as PropType<ScheduledExportItemFilters>,
      required: true,
    },
  },
  emits: ["update:filters"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const { exportType } = toRefs(props);
    const model: Ref<ScheduledExportItemFilters> = ref(cloneDeep(props.filters));
    const devicesStore = useDevicesStore();
    const devicesSettingsStore = useDevicesSettingsStore();
    const { osVersions: osVersionsFromStore, clientVersions: clientVersionsFromStore } =
      storeToRefs(devicesStore);
    const modelNotEmpty = computed(() => {
      return !isEmpty(model.value);
    });

    const triggers = computed<{ subheader?: boolean; name: string }[]>(() => {
      const ticketsFilters = model.value.tickets;
      return ticketsFilters.widget
        ? [
            {
              subheader: true,
              name: i18n.t(`tickets.filters.widgets.${ticketsFilters.widget}`),
            },
            ...sortTriggers(eventTriggerFilterMap[ticketsFilters.widget]).map((v) => ({
              name: v,
            })),
          ]
        : Object.entries(eventTriggerFilterMap).flatMap(([widget, triggers]) => [
            {
              subheader: true,
              name: i18n.t(`tickets.filters.widgets.${widget}`),
            },
            ...sortTriggers(triggers).map((v) => ({ name: v })),
          ]);
    });

    const sortTriggers = (triggers: TicketTrigger[]): TicketTrigger[] => {
      // If $te (Translation exists) take event trigger translation, else event type translation
      const getTriggerOrTypeTranslation = (item: string) =>
        i18n.te(`tickets.eventTriggers.${item}`)
          ? i18n.t(`tickets.eventTriggers.${item}`)
          : i18n.t(`tickets.eventTypes.${item}`);
      return triggers.slice().sort((a, b) => {
        return getTriggerOrTypeTranslation(a).localeCompare(getTriggerOrTypeTranslation(b));
      });
    };

    onMounted(async () => {
      await devicesStore.init();
    });

    watch(
      () => model.value,
      (newVal) => {
        emit("update:filters", newVal);
      },
      { deep: true }
    );

    watch(
      exportType,
      (newVal) => {
        switch (newVal) {
          case ExportCategory.ACTIVITY_LOGS:
            model.value.activityLogs = {
              ...defaultFiltersState.activityLogsFilters,
            };
            break;
          case ExportCategory.TICKETS:
            model.value.tickets = { ...defaultFiltersState.ticketsFilters };
            break;
          case ExportCategory.DEVICES:
            model.value.devices = { ...defaultFiltersState.devicesFilters };
            break;
          case ExportCategory.USERS:
            model.value.users = { ...defaultUsersExceptionsTabFilters };
            break;
        }
      },
      { deep: true }
    );

    return {
      model,
      modelNotEmpty,
      widgets: Object.values(TicketsModuleFilter),
      triggers,
      devicesStore,
      devicesSettingsStore,
      osVersionsFromStore,
      clientVersionsFromStore,
      ExportCategory,
      usersFilterItems: [
        Service.BOX,
        Service.DROPBOX,
        Service.G_SUITE,
        Service.OFFICE_365,
        Service.SLACK,
        Service.SALES_FORCE,
      ],
    };
  },
});
</script>

<style lang="scss" scoped>
.filter,
.filter-menu,
.range-picker {
  width: 240px !important;
}

:deep(*) {
  .clear-btn {
    display: none !important;
  }
}
</style>
